import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ImagePlus, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CHAT_RECOMMENDATIONS,
  isRecommendationVisibleForPersona,
  type ChatRecommendationDefinition,
} from "@/config/chatRecommendations";
import { deriveWorkspacePersona } from "@/config/workspacePersona";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

type ChatPlusMenuProps = {
  /** When set, renders the "Add files or photos" item and opens a file picker. */
  onFilesSelected?: (files: FileList) => void;
  /** Mirrors the disabled state of the chat input. */
  isDisabled?: boolean;
  /** Optional analytics hook fired when a tool is opened from this menu. */
  onOpenTool?: (rec: ChatRecommendationDefinition) => void;
  className?: string;
};

/**
 * The chat input's `+` affordance, expanded into a small action menu. Items:
 *   - "Add files or photos" (when `onFilesSelected` is provided)
 *   - Persona-available *tools* from `CHAT_RECOMMENDATIONS` (kind === "tool")
 *
 * Chat spaces (Reset, Reclaim, …) are intentionally NOT included here — those
 * are first-class entries in the sidebar and surfacing them in two places adds
 * noise. If we want them, flip the filter below to include `kind === "chat"`
 * (or add a `showInPlusMenu?: boolean` flag on the registry).
 */
export const ChatPlusMenu = ({
  onFilesSelected,
  isDisabled = false,
  onOpenTool,
  className,
}: ChatPlusMenuProps) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const persona = deriveWorkspacePersona(userData);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tools = useMemo(
    () =>
      CHAT_RECOMMENDATIONS.filter(
        (r) => r.kind === "tool" && isRecommendationVisibleForPersona(r, persona)
      ),
    [persona]
  );

  // If there's nothing to put in the menu, render nothing (the chat input keeps
  // working without a `+` button — same as the previous `onFilesSelected`-gated
  // behavior).
  if (!onFilesSelected && tools.length === 0) return null;

  const handleOpenTool = (rec: ChatRecommendationDefinition) => {
    onOpenTool?.(rec);
    if (rec.route) navigate(rec.route);
  };

  return (
    <>
      {onFilesSelected && (
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          aria-hidden
          onChange={(e) => {
            const list = e.target.files;
            if (list?.length) onFilesSelected(list);
            e.target.value = "";
          }}
        />
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Open chat actions"
            disabled={isDisabled}
            className={cn(
              "w-10 h-10 shrink-0 self-center rounded-full border border-gray-200 text-bodhi-teal",
              "flex items-center justify-center transition-colors",
              "hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bodhi-teal",
              className
            )}
          >
            <Plus className="w-5 h-5" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" side="top" className="w-64">
          {onFilesSelected && (
            <>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }}
                className="gap-2 py-2"
              >
                <ImagePlus className="h-4 w-4 text-bodhi-teal" aria-hidden />
                <span className="font-inter text-sm">Add files or photos</span>
              </DropdownMenuItem>
              {tools.length > 0 && <DropdownMenuSeparator />}
            </>
          )}

          {tools.length > 0 && (
            <>
              <DropdownMenuLabel className="font-inter text-[11px] uppercase tracking-wide text-stone-500">
                Tools
              </DropdownMenuLabel>
              {tools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <DropdownMenuItem
                    key={tool.id}
                    onSelect={() => handleOpenTool(tool)}
                    className="gap-2 py-2"
                  >
                    <Icon className="h-4 w-4 text-bodhi-teal" aria-hidden />
                    <span className="font-inter text-sm">{tool.label.replace(/^Open the /i, "")}</span>
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
